import { Filter, Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { FilterToolbarSearch } from '@/components/ui/FilterToolbarSearch'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/utils/helpers'
import type { ResidentBulkToolbarProps } from '../../types'

const TOOLTIP_MARK_PAID = 'The selected resident will be marked as paid.'
const TOOLTIP_NOT_PAID_ONLY =
  'The selected resident will be marked as not paid ONLY. A reimbursement request will not be opened at this time.'
const TOOLTIP_NOT_PAID_OPEN =
  'The selected resident will be marked as not paid and you will be directed to create a Reimbursement Request.'

/**
 * Resident list toolbar — search, then rent / export / edit / filters actions.
 */
export function ResidentBulkToolbar({
  selectedCount,
  searchValue,
  searchPlaceholder,
  searchLabel,
  onSearchChange,
  onSearchSubmit,
  onOpenFilters,
  activeFilterCount = 0,
  testIdPrefix = 'resident-list',
  canMultiEdit,
  canExport,
  canNotPaid,
  canPaid,
  onExport,
  onMultiEdit,
  onNotPaidOnly,
  onNotPaidOpenReimbursement,
  onPaid,
  paidLoading = false,
  notPaidOnlyLoading = false,
  notPaidOpenReimbursementLoading = false,
  columnsMenu,
}: ResidentBulkToolbarProps & {
  onSearchSubmit?: () => void
  columnsMenu?: ReactNode
}) {
  const notPaidBusy = notPaidOnlyLoading || notPaidOpenReimbursementLoading
  const disabled = selectedCount === 0 || paidLoading || notPaidBusy
  const hasFilters = activeFilterCount > 0

  return (
    <div
      data-testid={`${testIdPrefix}-toolbar`}
      className="flex w-full min-w-0 max-w-full flex-wrap items-center justify-between gap-2 sm:gap-3"
    >
      <FilterToolbarSearch
        data-testid={`${testIdPrefix}-search-wrap`}
        inputDataTestId={`${testIdPrefix}-search-input`}
        value={searchValue}
        onChange={onSearchChange}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            onSearchSubmit?.()
          }
        }}
        placeholder={searchPlaceholder}
        label={searchLabel}
        wrapClassName="relative min-w-[14rem] max-w-md flex-1 basis-[14rem] lg:max-w-lg"
        inputClassName="focus:border-blue-400 focus:ring-blue-100"
      />

      <div
        data-testid={`${testIdPrefix}-toolbar-actions`}
        className="flex max-w-full flex-1 basis-auto flex-wrap items-center justify-end gap-1.5 sm:gap-2 lg:flex-nowrap"
      >
        {canPaid ? (
          <Tooltip content={TOOLTIP_MARK_PAID} multiline side="bottom">
            <button
              type="button"
              data-testid="resident-bulk-paid-button"
              disabled={disabled}
              onClick={onPaid}
              className={cn(
                'inline-flex h-[42px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3.5 text-sm font-semibold text-white shadow-sm transition',
                'bg-[#34a853] hover:bg-[#2d8f47]',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {paidLoading ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
              ) : null}
              Mark Rent Paid
            </button>
          </Tooltip>
        ) : null}
        {canNotPaid ? (
          <>
            <Tooltip content={TOOLTIP_NOT_PAID_ONLY} multiline side="bottom">
              <button
                type="button"
                data-testid="resident-bulk-not-paid-only-button"
                disabled={disabled}
                onClick={onNotPaidOnly}
                className={cn(
                  'inline-flex h-[42px] max-w-full cursor-pointer items-center justify-center gap-2 rounded-lg px-3.5 text-sm font-semibold text-white shadow-sm transition',
                  'bg-[#f59e0b] hover:bg-[#d97706]',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {notPaidOnlyLoading ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                ) : null}
                <span className="text-center leading-tight">
                  Mark Rent Not Paid Only (open Reimbursement later)
                </span>
              </button>
            </Tooltip>
            <Tooltip content={TOOLTIP_NOT_PAID_OPEN} multiline side="bottom">
              <button
                type="button"
                data-testid="resident-bulk-open-reimbursement-button"
                disabled={disabled}
                onClick={onNotPaidOpenReimbursement}
                className={cn(
                  'inline-flex h-[42px] max-w-full cursor-pointer items-center justify-center gap-2 rounded-lg px-3.5 text-sm font-semibold text-white shadow-sm transition',
                  'bg-[#dc2626] hover:bg-[#b91c1c]',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                {notPaidOpenReimbursementLoading ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                ) : null}
                <span className="text-center leading-tight">
                  Mark Rent Not Paid &amp; Open Reimbursement
                </span>
              </button>
            </Tooltip>
          </>
        ) : null}
        {canExport ? (
          <button
            type="button"
            data-testid="resident-bulk-export-button"
            onClick={onExport}
            className={cn(
              'inline-flex h-[42px] cursor-pointer items-center justify-center whitespace-nowrap rounded-lg px-3.5 text-sm font-semibold text-white shadow-sm transition',
              'bg-[#1597ff] hover:bg-[#0d7acc]',
            )}
          >
            Export
          </button>
        ) : null}
        {canMultiEdit ? (
          <button
            type="button"
            data-testid="resident-bulk-multi-edit-button"
            disabled={disabled}
            onClick={onMultiEdit}
            className={cn(
              'inline-flex h-[42px] cursor-pointer items-center justify-center whitespace-nowrap rounded-lg px-3.5 text-sm font-semibold text-white shadow-sm transition',
              'bg-[#1597ff] hover:bg-[#0d7acc]',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            Multi-Edit
          </button>
        ) : null}

        {columnsMenu}

        <button
          type="button"
          data-testid={`${testIdPrefix}-filters-open-button`}
          onClick={onOpenFilters}
          className={cn(
            'relative inline-flex h-[42px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border bg-white px-4 text-sm font-semibold shadow-sm transition-all hover:bg-slate-50 hover:shadow-md',
            hasFilters
              ? 'border-blue-400 text-blue-700'
              : 'border-slate-200 text-slate-700',
          )}
        >
          <Filter className="h-[18px] w-[18px] shrink-0" />
          Filters
          {hasFilters ? (
            <span
              data-testid={`${testIdPrefix}-filters-active-count-badge`}
              className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[rgb(21_151_255)] px-1.5 text-[10px] font-bold leading-none text-white"
            >
              {activeFilterCount}
            </span>
          ) : null}
        </button>
      </div>
    </div>
  )
}
